// @flow
import * as React from 'react'
import Form from '../../Form'
import Alert from '../../../core/Alert'
import PaymentRequestMethod from '../PaymentRequestMethod'
import AlipayMethod from '../AlipayMethod'
import ChargeProviderMethod from '../ChargeProviderMethod'
import UsedCardMethod from '../UsedCardMethod'
import SavedCardsMethod from '../SavedCardsMethod'
import NewCreditCardMethod from '../NewCreditCardMethod'
import DirectBillMethod from '../DirectBillMethod'
import { FormattedMessage } from 'mz-intl'
import messages from '../messages.intl'
import {
  ConfirmationRow,
  Container,
  ErrorContainer,
  MethodContainer,
  BeforeButtonWrapper,
  CurrencySupport,
} from './elements'
import { ASYNC_PAYMENT_METHOD, USD_CURRENCY } from '../constants'

// Types
import type { MethodProps, PaymentResult, CardBrandType } from '..'
import type { Meta } from 'mz-ui-kit/core/BaseField'

export type Props = MethodProps

export type PaymentMethodType = $Keys<typeof SUPPORTED_METHODS>
type Values = { option: PaymentMethodValue }
type State = { loading: boolean, meta: Meta }
export type PaymentMethodValue =
  | PaymentResult
  | {
      type: typeof ASYNC_PAYMENT_METHOD,
      getOption: () => Promise<PaymentResult>,
      brand?: CardBrandType,
    }

// Constants
export const SUPPORTED_METHODS = {
  paymentRequest: PaymentRequestMethod,
  alipay: AlipayMethod,
  savedCard: SavedCardsMethod,
  newCard: NewCreditCardMethod,
  usedCard: UsedCardMethod,
  chargeProvider: ChargeProviderMethod,
  directBill: DirectBillMethod,
}
const DEFAULT_METHOD: PaymentMethodType[] = ['savedCard', 'newCard']

/**
 * Component that shows new and used credit card options. It also
 * validate the selected options and if nothing selected it shows
 * an error.
 * @extends React
 */
class PaymentMethodsList extends React.Component<Props, State> {
  state = { loading: false, meta: {} }

  brand = null

  get filteredPaymentMethods(): Array<any> {
    const { allowedPaymentMethods } = this.props
    const actualMethodsKeys = allowedPaymentMethods || DEFAULT_METHOD
    return actualMethodsKeys
      .map((name) => SUPPORTED_METHODS[name])
      .filter((x) => !!x)
  }

  validateSelectedOption = (value: any) => {
    if (!value || !value.option) {
      return messages.SELECT_PAYMENT_METHOD
    }
    return null
  }

  handleSubmit = (values: ?Values) => {
    const { onClick } = this.props
    const option = values && values.option
    if (!option) return

    onClick &&
      onClick(async () => {
        if (option.type === ASYNC_PAYMENT_METHOD) {
          try {
            this.setState({ loading: true })
            const result = await option.getOption()
            return result
          } finally {
            this.setState({ loading: false })
          }
        } else {
          return option
        }
      })
  }

  handleChangeFormValue = (value: Values) => {
    const { onChangeCardBrand } = this.props
    const newBrand = value.option.brand

    if (newBrand !== this.brand) {
      this.brand = newBrand
      onChangeCardBrand && onChangeCardBrand(newBrand)
    }
  }

  handleChangeMeta = (meta: Meta) => {
    this.setState({ meta })
  }

  changeMeta = (values: Meta) => {
    this.setState({
      meta: {
        ...this.state.meta,
        ...values,
      },
    })
  }

  render() {
    const {
      renderBeforeButton,
      renderButton,
      displayAmount,
      originalCurrency,
      currency,
      confirmLabel,
      shouldShowCurrencySupportMessage = true,
    } = this.props

    const loading = this.props.loading || this.state.loading
    const disabled = loading || this.props.disabled

    const thingsBeforeButton = !!renderBeforeButton && renderBeforeButton()

    const currencySupportMessage = shouldShowCurrencySupportMessage &&
      ((originalCurrency && currency !== originalCurrency) ||
        (originalCurrency && originalCurrency !== USD_CURRENCY)) && (
        <CurrencySupport>
          <Alert color="yellow">
            <FormattedMessage
              message={messages.CURRENCY_SUPPORT_MESSAGE}
              values={{ currency }}
            />
          </Alert>
        </CurrencySupport>
      )

    return (
      <Form
        validate={this.validateSelectedOption}
        onChange={this.handleChangeFormValue}
        onMetaChange={this.handleChangeMeta}
        meta={this.state.meta}
        onSubmit={this.handleSubmit}
        render={({ submit, meta }, nest) => (
          <Container>
            {this.filteredPaymentMethods.map((MethodComponent, i) => (
              <MethodContainer key={i}>
                <MethodComponent
                  {...this.props}
                  {...nest('option', { dependsOnValue: true })}
                  changeMeta={this.changeMeta}
                />
              </MethodContainer>
            ))}
            {!meta.valid && meta.touched && (
              <ErrorContainer>
                <Alert color="red">
                  <FormattedMessage message={meta.error} />
                </Alert>
              </ErrorContainer>
            )}
            {meta.warning && (
              <ErrorContainer>
                <Alert color="yellow">
                  <FormattedMessage message={meta.warning} />
                </Alert>
              </ErrorContainer>
            )}
            {thingsBeforeButton && (
              <BeforeButtonWrapper>{thingsBeforeButton}</BeforeButtonWrapper>
            )}
            {currencySupportMessage}
            <ConfirmationRow>
              {renderButton({
                onClick: submit,
                loading,
                disabled: disabled || meta.deepLoading,
                displayAmount,
                confirmLabel,
              })}
            </ConfirmationRow>
          </Container>
        )}
      />
    )
  }
}

export default PaymentMethodsList
